import type { FormEvent } from "react";
import React from "react";
import googleIcon from "./google.svg";
import seznamIcon from "./seznam.svg";
import styles from "./style.module.scss";
import { readCookie } from "repositories/helpers";
import type SimplePortalSettings from "entities/simplePortalSettings";

interface OmniAuthButtonProps {
  text: string;
  provider: "seznam" | "google";
  origin?: "login" | "registration" | "create_link";
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void; // Optional callback for custom submit handling
  setting: SimplePortalSettings;
  data?: Record<string, string>; // Additional data to be sent with the form
}

const BASE_PATH = "/api/v1/user/auth/";

// Rails-like form-button with CSRF token
const OmniAuthButton: React.FC<OmniAuthButtonProps> = ({ data, text, provider, onSubmit, setting, origin }) => {
  if (!setting || setting.disabled) {
    return null;
  }

  const [initialToken] = React.useState(() => readCookie("X-CSRF-Token")); // read the cookie once
  const hiddenInputRef = React.useRef<HTMLInputElement>(null);

  let iconSrc: string;
  let providerStyle: string;

  if (provider === "seznam") {
    iconSrc = seznamIcon;
    providerStyle = styles.seznamSignInButton;
  } else {
    iconSrc = googleIcon;
    providerStyle = styles.googleSignInButton;
  }

  data = data ?? {};
  if (origin) {
    data.origin = origin;
  }
  let queryString = new URLSearchParams(data).toString();
  if (queryString) {
    queryString = `?${queryString}`;
  }

  const omniauthSuffix = `${provider}_oauth2`; // the omniauth provider key
  const href = `${BASE_PATH}/${omniauthSuffix}${queryString}`;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (onSubmit) {
      onSubmit(event);
    }

    const freshToken = readCookie("X-CSRF-Token");
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = decodeURIComponent(freshToken ?? ""); // try to get the latest CSRF token
    }

    //Normal form submit continues - Sends POST
  };

  return (
    <form method="post" id={`${provider}-form`} action={href} onSubmit={handleSubmit} style={{ flexGrow: "1" }}>
      <button className={`${styles.omniauthButton} ${providerStyle}`} type="submit" form={`${provider}-form`} formAction={href}>
        <img src={iconSrc} />
        {text}
      </button>
      <input
        ref={hiddenInputRef}
        name="authenticity_token"
        type="hidden"
        value={decodeURIComponent(initialToken ?? "")}
        readOnly
      />
    </form>
  );
};

export default OmniAuthButton;
